import React from 'react';
import { FormGroup, Label, Input, Row, Col, FormFeedback } from 'reactstrap';
import DatePicker from 'react-datepicker';

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForDisciplinaryAction: string;
  actionTaken: string;
  expectedImprovement: string;
  followUpMeetingDate: string;
  privateNotes: string;
}

interface FormErrors {
  name?: string;
  date?: string;
  policyViolationIds?: string;
  reasonForDisciplinaryAction?: string;
  actionTaken?: string;
  expectedImprovement?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface UserInfoSectionProps {
  formData: FormData;
  errors: FormErrors;
  onChange: (field: keyof FormData, value: string) => void;
}

export const UserInfoSection = ({ formData, errors, onChange }: UserInfoSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Employee Information</h5>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            invalid={!!errors.name}
            readOnly
            disabled
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="date">
            Date <span className="text-danger">*</span>
          </Label>
          <Input
            tag={DatePicker}
            dateFormat="MM/dd/yyyy"
            id="date"
            selected={formData.date ? new Date(formData.date + 'T00:00:00') : null}
            onChange={(d: any) => onChange('date', d ? d.toISOString().split('T')[0] : '')}
            maxDate={new Date()}
            placeholderText="Select date"
            autoComplete="off"
          />
          {errors.date && <FormFeedback>{errors.date}</FormFeedback>}
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="position">Position</Label>
          <Input
            type="text"
            id="position"
            value={formData.position}
            onChange={(e) => onChange('position', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="department">Department</Label>
          <Input
            type="text"
            id="department"
            value={formData.department}
            onChange={(e) => onChange('department', e.target.value)}
            readOnly
            disabled
          />
        </FormGroup>
      </Col>
    </Row>
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="supervisor">Supervisor</Label>
          <Input
            type="text"
            id="supervisor"
            value={formData.supervisor}
            readOnly
            onChange={(e) => onChange('supervisor', e.target.value)}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="dateOfHire">Date of Hire (Read-only)</Label>
          <Input type="date" id="dateOfHire" value={formData.dateOfHire} readOnly disabled />
        </FormGroup>
      </Col>
    </Row>
  </div>
);

interface PolicySectionProps {
  formData: FormData;
  errors: FormErrors;
  policyViolations: PolicyViolation[];
  onChange: (field: keyof FormData, value: string | string[]) => void;
}

export const PolicySection = ({ formData, errors, policyViolations, onChange }: PolicySectionProps) => {
  const handleCheckboxChange = (violationId: string) => {
    const newIds = formData.policyViolationIds.includes(violationId)
      ? formData.policyViolationIds.filter((id) => id !== violationId)
      : [...formData.policyViolationIds, violationId];
    onChange('policyViolationIds', newIds);
  };

  return (
    <div className="mb-4">
      <h5 className="text-primary mb-3">Policy Violations</h5>
      <FormGroup>
        <Label>
          Policy Violations <span className="text-danger">*</span>
        </Label>
        <div>
          {policyViolations.map((violation) => (
            <FormGroup check key={violation.id} className="mb-2">
              <Label check>
                <Input
                  type="checkbox"
                  checked={formData.policyViolationIds.includes(violation.id)}
                  onChange={() => handleCheckboxChange(violation.id)}
                />
                {violation.name}
              </Label>
            </FormGroup>
          ))}
        </div>
        {errors.policyViolationIds && (
          <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {errors.policyViolationIds}
          </div>
        )}
      </FormGroup>
    </div>
  );
};

interface ReasonForDisciplinaryActionSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const ReasonForDisciplinaryActionSection = ({
  value,
  error,
  onChange,
}: ReasonForDisciplinaryActionSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Reason for Disciplinary Action</h5>
    <FormGroup>
      <Label for="reasonForDisciplinaryAction">
        Description <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="reasonForDisciplinaryAction"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Description of the issue or behavior that led to the disciplinary action"
      />
      <small className="form-text text-muted">
        Description of the issue or behavior that led to the disciplinary action
      </small>
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface ActionTakenSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const ActionTakenSection = ({ value, error, onChange }: ActionTakenSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Action Taken</h5>
    <FormGroup>
      <Label for="actionTaken">
        Description <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="actionTaken"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Description of the action taken (e.g., Written Warning, Suspension, Final Warning, etc.)"
      />
      <small className="form-text text-muted">
        Description of the action taken (e.g., Written Warning, Suspension, Final Warning, etc.)
      </small>
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface ExpectedImprovementSectionProps {
  value: string;
  error?: string;
  onChange: (value: string) => void;
}

export const ExpectedImprovementSection = ({ value, error, onChange }: ExpectedImprovementSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Expected Improvement</h5>
    <FormGroup>
      <Label for="expectedImprovement">
        Description <span className="text-danger">*</span>
      </Label>
      <Input
        type="textarea"
        id="expectedImprovement"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        invalid={!!error}
        placeholder="Set clear expectations for improvement and timelines"
      />
      <small className="form-text text-muted">
        Set clear expectations for improvement and timelines. Include regular check-ins if necessary
      </small>
      {error && <FormFeedback>{error}</FormFeedback>}
    </FormGroup>
  </div>
);

interface FollowUpMeetingDateSectionProps {
  value: string;
  onChange: (value: string) => void;
}

export const FollowUpMeetingDateSection = ({ value, onChange }: FollowUpMeetingDateSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Follow Up Meeting Date</h5>
    <FormGroup>
      <Label for="followUpMeetingDate">Date</Label>
      <Input
        tag={DatePicker}
        dateFormat="MM/dd/yyyy"
        id="followUpMeetingDate"
        selected={value ? new Date(value + 'T00:00:00') : null}
        onChange={(d: any) => onChange(d ? d.toISOString().split('T')[0] : '')}
        placeholderText="Select date"
        autoComplete="off"
      />
      <small className="form-text text-muted">This date will appear in the final PDF sentence.</small>
    </FormGroup>
  </div>
);

interface PrivateNotesSectionProps {
  value: string;
  onChange: (value: string) => void;
}

export const PrivateNotesSection = ({ value, onChange }: PrivateNotesSectionProps) => (
  <div className="mb-4">
    <h5 className="text-primary mb-3">Private Notes</h5>
    <FormGroup>
      <Label for="privateNotes">Private Notes</Label>
      <Input
        type="textarea"
        id="privateNotes"
        rows={6}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Private notes only seen by Director and Upper Management"
      />
      <small className="form-text text-muted">Only seen by Director and Upper Management</small>
    </FormGroup>
  </div>
);
